/**
 * Service de persistance des données de formulaire
 * Sauvegarde progressive pour éviter la perte de données
 */

import { logger } from '@/utils/productionLogger';
import type { ReferentFormData } from '@/types/referentForm';

interface FormState {
  data: Partial<ReferentFormData>;
  lastSaved: Date;
  referentId: string;
  version: number;
}

class FormPersistenceService {
  private readonly STORAGE_KEY_PREFIX = 'referent_form_';
  private readonly AUTO_SAVE_INTERVAL = 30000; // 30 secondes
  private readonly MAX_STORAGE_AGE = 24 * 60 * 60 * 1000; // 24 heures
  
  private autoSaveTimers: Map<string, NodeJS.Timeout> = new Map();

  /**
   * Sauvegarde les données du formulaire
   */
  saveFormData(referentId: string, formData: Partial<ReferentFormData>): void {
    try {
      const storageKey = this.getStorageKey(referentId);
      
      const formState: FormState = {
        data: formData,
        lastSaved: new Date(),
        referentId,
        version: 1
      };

      localStorage.setItem(storageKey, JSON.stringify(formState));
      
      logger.debug('[FormPersistence] Form data saved', {
        referentId,
        fieldsCount: Object.keys(formData).length,
        timestamp: formState.lastSaved
      });

    } catch (_error) {
      logger.warn('[FormPersistence] Failed to save form data', {
        error: error as Error,
        referentId
      });
    }
  }

  /**
   * Récupère les données sauvegardées du formulaire
   */
  loadFormData(referentId: string): Partial<ReferentFormData> | null {
    try {
      const storageKey = this.getStorageKey(referentId);
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) return null;

      const formState: FormState = JSON.parse(stored);
      
      // Vérifier l'âge des données
      const age = Date.now() - new Date(formState.lastSaved).getTime();
      if (age > this.MAX_STORAGE_AGE) {
        this.clearFormData(referentId);
        logger.info('[FormPersistence] Expired form data cleared', {
          referentId,
          age: Math.round(age / 1000 / 60) + ' minutes'
        });
        return null;
      }

      logger.info('[FormPersistence] Form data loaded', {
        referentId,
        fieldsCount: Object.keys(formState.data).length,
        lastSaved: formState.lastSaved
      });

      return formState.data;

    } catch (_error) {
      logger.warn('[FormPersistence] Failed to load form data', {
        error: _error as Error,
        referentId
      });
      return null;
    }
  }

  /**
   * Démarre la sauvegarde automatique
   */
  startAutoSave(referentId: string, getFormData: () => Partial<ReferentFormData>): void {
    // Nettoyer le timer existant
    this.stopAutoSave(referentId);

    const timer = setInterval(() => {
      const formData = getFormData();
      if (formData && Object.keys(formData).length > 0) {
        this.saveFormData(referentId, formData);
      }
    }, this.AUTO_SAVE_INTERVAL);

    this.autoSaveTimers.set(referentId, timer);
    
    logger.debug('[FormPersistence] Auto-save started', {
      referentId,
      interval: this.AUTO_SAVE_INTERVAL
    });
  }

  /**
   * Arrête la sauvegarde automatique
   */
  stopAutoSave(referentId: string): void {
    const timer = this.autoSaveTimers.get(referentId);
    if (timer) {
      clearInterval(timer);
      this.autoSaveTimers.delete(referentId);
      
      logger.debug('[FormPersistence] Auto-save stopped', { referentId });
    }
  }

  /**
   * Supprime les données sauvegardées
   */
  clearFormData(referentId: string): void {
    try {
      const storageKey = this.getStorageKey(referentId);
      localStorage.removeItem(storageKey);
      this.stopAutoSave(referentId);
      
      logger.debug('[FormPersistence] Form data cleared', { referentId });
    } catch (_error) {
      logger.warn('[FormPersistence] Failed to clear form data', {
        error: _error as Error,
        referentId
      });
    }
  }

  /**
   * Vérifie si des données sauvegardées existent
   */
  hasStoredData(referentId: string): boolean {
    try {
      const storageKey = this.getStorageKey(referentId);
      const stored = localStorage.getItem(storageKey);
      return stored !== null;
    } catch {
      return false;
    }
  }

  /**
   * Nettoie toutes les données expirées
   */
  cleanupExpiredData(): void {
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.STORAGE_KEY_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const formState: FormState = JSON.parse(stored);
              const age = Date.now() - new Date(formState.lastSaved).getTime();
              
              if (age > this.MAX_STORAGE_AGE) {
                keysToRemove.push(key);
              }
            }
          } catch {
            // Données corrompues, les supprimer
            keysToRemove.push(key);
          }
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        logger.info('[FormPersistence] Cleaned up expired data', {
          removedCount: keysToRemove.length
        });
      }

    } catch (_error) {
      logger.warn('[FormPersistence] Failed to cleanup expired data', {
        error: _error as Error
      });
    }
  }

  private getStorageKey(referentId: string): string {
    return `${this.STORAGE_KEY_PREFIX}${referentId}`;
  }
}

// Instance singleton
export const formPersistence = new FormPersistenceService();

// Nettoyage automatique au démarrage et toutes les heures
if (typeof window !== 'undefined') {
  // Nettoyage initial
  formPersistence.cleanupExpiredData();
  
  // Nettoyage périodique
  setInterval(() => {
    formPersistence.cleanupExpiredData();
  }, 60 * 60 * 1000); // 1 heure
}